'use client'

import { type Cruz, lookup } from '@cruz-com/lookup'
import { Popover, PopoverBackdrop, PopoverButton, PopoverPanel } from '@headlessui/react'
import _ from 'lodash'
import Link from 'next/link'
import { memo } from 'react'
import { createPortal } from 'react-dom'
import { HiChevronDown } from 'react-icons/hi'
import slugify from 'slugify'
import { twMerge } from 'tailwind-merge'
import { CruzIcon, Divider } from '@/components'
import { highlyRatedVehicles, pages, popularCategories, vehicleSitemapPages } from '@/settings'
import { useGlobalStore } from '@/store/provider'
import { ToggleBodyScroll } from './toggleBodyScroll'

type MenuProps = {
  name?: string
  menu?: string | React.ReactNode
  icon?: React.ReactNode
  href: string
  count?: number | undefined
  className?: string
  ariaLabel?: string
  onClick: () => void
}

// Components
const MenuItem = memo(({ name, menu, href, count, icon, className, ariaLabel, onClick }: MenuProps) => (
  <Link
    hrefLang='en'
    key={href}
    href={href}
    aria-label={ariaLabel || name || (typeof menu === 'string' ? menu : undefined)}
    className={twMerge('menu-item', className)}
    onClick={onClick}
  >
    <div className='flex items-center gap-1.5'>
      {icon}
      {menu || name}
      {!_.isUndefined(count) && <span className='nav-count ml-1'>{count}</span>}
    </div>
  </Link>
))

const SearchMenuItem = memo(({ onClick }: { onClick: () => void }) => (
  <section className='grid grid-cols-2 divide-x rounded-t-md'>
    <MenuItem
      {...pages.vehicleSearch}
      className='py-4!'
      onClick={onClick}
      menu={
        <span className='flex items-center'>
          <CruzIcon.Search className='mr-2 size-6' />
          Search All Vehicles
        </span>
      }
    />
    <MenuItem
      {...pages.vehicleAiSearch}
      onClick={onClick}
      className='py-4!'
      menu={
        <span className='flex items-center'>
          <CruzIcon.AiLight className='mr-2 size-6' />
          {pages.vehicleAiSearch.name}
        </span>
      }
    />
  </section>
))

const SavedVehiclesSection = memo(({ user, onClose }: { user: Cruz.UserDisplay; onClose: () => void }) => (
  <section className='grid grid-cols-3 divide-x rounded-t-md'>
    <MenuItem
      {...(_.isEmpty(user) ? pages.vehicleRecentlyViewed : pages.myRecentlyViewed)}
      menu='Recently Viewed'
      className='rounded-tr-md py-4!'
      onClick={onClose}
    />
    <MenuItem
      {...pages.mySavedVehicle}
      menu='Saved Vehicles'
      count={user?.statistic?.saved_vehicle}
      className='rounded-tl-md py-4!'
      onClick={onClose}
    />
    <MenuItem
      {...pages.mySavedSearch}
      menu='Saved Searches'
      count={user?.statistic?.saved_search}
      className='rounded-tr-md py-4!'
      onClick={onClose}
    />
  </section>
))

const PopularCategoriesSection = memo(({ onClose }: { onClose: () => void }) => (
  <section className='divide-y'>
    <div className='menu-category'>By Popular Categories</div>
    <ul className='mb-4 pt-2'>
      {_.map(popularCategories, (d, idx) => (
        <MenuItem key={`pc_${idx}`} {...d} onClick={onClose} />
      ))}
      <MenuItem {...pages.vehiclePrivate} onClick={onClose} />
    </ul>
  </section>
))

const BodyStylesSection = memo(({ onClose }: { onClose: () => void }) => (
  <section className='divide-y'>
    <div className='menu-category'>By Body Styles</div>
    <ul className='pt-2 pb-3'>
      {_.map(lookup.vehicle.bodyType, (d, idx) => (
        <MenuItem
          key={`vBodyType_${idx}`}
          href={`/vehicles/body-style-s/${slugify(d.sName, { lower: true })}/${d.id}`}
          menu={<span className='truncate'>{d.sName}</span>}
          onClick={onClose}
        />
      ))}
    </ul>
  </section>
))

const HighlyRatedSection = memo(({ onClose }: { onClose: () => void }) => (
  <section className='divide-y'>
    <div className='menu-category'>Highly Rated</div>
    <ul className='pt-2 pb-3'>
      {_.map(highlyRatedVehicles, (d, idx) => (
        <MenuItem key={`vHighlyRated_${idx}`} href={d.href} menu={<span className='truncate'>{d.menu}</span>} onClick={onClose} />
      ))}
    </ul>
  </section>
))

const MaxSeatingSection = memo(({ onClose }: { onClose: () => void }) => (
  <section className='divide-y'>
    <div className='menu-category border-t'>By Max Seatings</div>
    <ul className='pt-2 pb-3'>
      {_.map(lookup.vehicle.seats, (d, idx) => (
        <MenuItem
          key={`vMaxSeating_${idx}`}
          href={`/vehicles/seats/${d.name}/${d.id}`}
          menu={<span className='truncate'>{d.name} Seats</span>}
          onClick={onClose}
        />
      ))}
    </ul>
  </section>
))

const BrowseSection = memo(({ onClose }: { onClose: () => void }) => (
  <section className='divide-y'>
    <div className='menu-category border-t'>Browse More</div>
    <ul className='pt-2 pb-5'>
      {_.map(vehicleSitemapPages, (d, idx) => (
        <MenuItem key={`vSitemapPage_${idx}`} href={d.href} menu={<span className='truncate'>{d.name}</span>} onClick={onClose} />
      ))}
    </ul>
  </section>
))

export const HeaderVehicleMenu = memo(() => {
  const { user } = useGlobalStore((state) => state)

  return (
    <Popover as='div' className='relative h-full'>
      {({ open, close }) => (
        <>
          <ToggleBodyScroll open={open} />
          {open &&
            typeof document !== 'undefined' &&
            createPortal(<PopoverBackdrop className='dialog-backdrop z-10!' onClick={close} />, document.body)}
          <PopoverButton className='menu-button font-semibold dark:font-medium' aria-label='Vehicles Menu'>
            <span>Vehicles</span>
            <HiChevronDown aria-hidden='true' className='-mr-1 ml-1 size-4' />
          </PopoverButton>
          <PopoverPanel
            as='nav'
            aria-label='Vehicles Submenu'
            anchor='bottom start'
            transition={true}
            modal={false}
            unmount={false}
            className='menu-items rounded-r-md'
          >
            <SearchMenuItem onClick={close} />
            <Divider />
            <SavedVehiclesSection user={user || {}} onClose={close} />
            <Divider />
            <div className='grid grid-flow-col divide-x'>
              <BodyStylesSection onClose={close} />
              <PopularCategoriesSection onClose={close} />
              <div>
                <HighlyRatedSection onClose={close} />
                <MaxSeatingSection onClose={close} />
                <BrowseSection onClose={close} />
              </div>
            </div>
          </PopoverPanel>
        </>
      )}
    </Popover>
  )
})
